import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Settings, Smartphone, Wifi, Shield, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useLanguage } from '../LanguageProvider';

type Lang = 'tr' | 'en';
type Localized<T> = Record<Lang, T>;

type Specification = { 
  label: Localized<string>; 
  value: Localized<string>;
};

type Project = {
  title: Localized<string>;
  category: Localized<string>;
  description: Localized<string>;
  image: string;
  features: Localized<string[]>;
  specifications: Specification[];
  useCases: Localized<string[]>;
  technologies: string[];
  warnings?: Localized<string[]>;
  androidAppUrl?: string;
  detailedUseCases?: { 
    title: Localized<string>; 
    description: Localized<string>;
    points?: Localized<string[]>;
  }[];
};

export function ProjectDetail() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const getLocalized = <T,>(value: Localized<T>): T => value[language] ?? value.tr;

  const texts = {
    tr: {
      backToProducts: 'Ürünlere Dön',
      exploreMobileApp: 'Mobil Uygulamayı Keşfedin',
      features: 'Özellikler',
      technicalSpecs: 'Teknik Özellikler',
      connection: 'Bağlantı',
      location: 'Konum',
      sensors: 'Sensörler',
      temperatureHumidity: 'Sıcaklık ve Nem',
      battery: 'Batarya',
      general: 'Genel',
      packageContents: 'Kutu İçeriği',
      warningsTitle: 'Kullanımdan Önce Dikkat Edilmesi Gerekenler',
      useCases: 'Kullanım Alanları',
      productDocumentation: 'Ürün Dokümantasyonu',
      viewPdf: 'PDF\'i Görüntüle',
      technologies: 'Kullanılan Teknolojiler',
      packageItem1: 'ShalterOn Kontrol Cihazı',
      packageItem2: '3M Anten',
      packageItem3: '12VDC 1A Adaptör',
      quantity: 'adet',
      ctaTitle: 'Bu Proje Hakkında Daha Fazla Bilgi Almak İster misiniz?',
      ctaDescription: 'Teknik detaylar, fiyat bilgisi ve kurulum süreci hakkında uzman ekibimizden bilgi alın.',
      visitWebsite: 'Frigolive Web Sitesini Ziyaret Edin',
      contactUs: 'İletişime Geçin',
      viewOtherProjects: 'Diğer Projeleri İncele',
      projectNotFound: 'Proje Bulunamadı',
      projectNotFoundDesc: 'Aradığınız proje mevcut değil.'
    },
    en: {
      backToProducts: 'Back to Products',
      exploreMobileApp: 'Explore Mobile App',
      features: 'Features',
      technicalSpecs: 'Technical Specifications',
      connection: 'Connection',
      location: 'Location',
      sensors: 'Sensors',
      temperatureHumidity: 'Temperature and Humidity',
      battery: 'Battery',
      general: 'General',
      packageContents: 'Package Contents',
      warningsTitle: 'Important Notes Before Use',
      useCases: 'Use Cases',
      productDocumentation: 'Product Documentation',
      viewPdf: 'View PDF',
      technologies: 'Technologies Used',
      packageItem1: 'ShalterOn Control Device',
      packageItem2: '3M Antenna',
      packageItem3: '12VDC 1A Adapter',
      quantity: 'unit',
      ctaTitle: 'Would You Like More Information About This Project?',
      ctaDescription: 'Get information about technical details, pricing, and installation process from our expert team.',
      visitWebsite: 'Visit Frigolive Website',
      contactUs: 'Contact Us',
      viewOtherProjects: 'View Other Projects',
      projectNotFound: 'Project Not Found',
      projectNotFoundDesc: 'The project you are looking for does not exist.'
    }
  } as const;

  const t = texts[language] ?? texts.tr;

  const projects: Record<string, Project> = {
    'frigolive': {
      title: {
        tr: 'Frigolive F-007',
        en: 'Frigolive F-007'
      },
      category: {
        tr: 'IoT & Sensörler',
        en: 'IoT & Sensors'
      },
      description: {
        tr: 'Araç ve lojistik sektörü için geliştirilmiş, GSM tabanlı canlı konum, sıcaklık, nem, ışık ve darbe verilerini izleyerek lojistik ve hassas ürün transferi süresince ürün bütünlüğünü ve güvenliğini garanti altına alın.',
        en: 'Developed for the automotive and logistics sector, ensure product integrity and safety during logistics and sensitive product transfers by monitoring real-time location, temperature, humidity, light and shock data with GSM-based system.'
      },
      image: '/frigolive-2.png',
      features: {
        tr: [
          'Canlı Sıcaklık Takibi',
          'Hücresel Bağlantı',
          'Canlı Konum Takibi',
          'Web Tabanlı Raporlama',
          'Uzun Batarya Ömrü',
          'Mobil Uygulama Desteği',
          'Su Geçirmez Tasarım',
          'Kolay Kullanım'
        ],
        en: [
          'Live Temperature Tracking',
          'Cellular Connectivity',
          'Live Location Tracking',
          'Web-Based Reporting',
          'Long Battery Life',
          'Mobile App Support',
          'Waterproof Design',
          'Easy to Use'
        ]
      },
      specifications: [
        // Bağlantı
        { 
          label: { tr: 'İletişim Aralığı', en: 'Communication Interval' }, 
          value: { tr: '5 dakika - 24 saat (seçilebilir aralık)', en: '5 minutes - 24 hours (selectable interval)' }
        },
        
        // Konum
        { 
          label: { tr: 'Konumlama Yöntemi', en: 'Positioning Method' }, 
          value: { tr: 'GPS, WiFi, Hücresel Üçgenleme', en: 'GPS, WiFi, Cellular Triangulation' }
        },
        { 
          label: { tr: 'Konum Doğruluğu', en: 'Location Accuracy' }, 
          value: { tr: 'GPS: 10 m, WiFi: 10 m, Hücresel: 500 m', en: 'GPS: 10 m, WiFi: 10 m, Cellular: 500 m' }
        },
        
        // Sensörler
        { 
          label: { tr: 'Sensörler', en: 'Sensors' }, 
          value: { tr: 'Sıcaklık, Nem, Işık, Darbe', en: 'Temperature, Humidity, Light, Shock' }
        },
        { 
          label: { tr: 'Ölçüm Kapasitesi', en: 'Measurement Capacity' }, 
          value: { tr: '30.000 kayda kadar', en: 'Up to 30,000 records' }
        },
        { 
          label: { tr: 'Kalibrasyon', en: 'Calibration' }, 
          value: { tr: 'ISO/IEC 17025 akredite, NIST izlenebilir', en: 'ISO/IEC 17025 accredited, NIST traceable' }
        },
        
        // Sıcaklık ve Nem
        { 
          label: { tr: 'Çalışma Sıcaklığı Aralığı', en: 'Operating Temperature Range' }, 
          value: { tr: '-25°C ile +60°C', en: '-25°C to +60°C' }
        },
        { 
          label: { tr: 'Nem Aralığı', en: 'Humidity Range' }, 
          value: { tr: '0%-100% RH', en: '0%-100% RH' }
        },
        { 
          label: { tr: 'Sıcaklık Hassasiyeti', en: 'Temperature Accuracy' }, 
          value: { tr: '±0.1°C', en: '±0.1°C' }
        },
        { 
          label: { tr: 'Nem Hassasiyeti', en: 'Humidity Accuracy' }, 
          value: { tr: '±2% RH', en: '±2% RH' }
        },
        { 
          label: { tr: 'Sıcaklık Sürüklenmesi', en: 'Temperature Drift' }, 
          value: { tr: 'Yılda <0.03°C', en: '<0.03°C per year' }
        },
        { 
          label: { tr: 'Nem Sürüklenmesi', en: 'Humidity Drift' }, 
          value: { tr: 'Yılda %0.25', en: '0.25% per year' }
        },
        { 
          label: { tr: 'Düşük Sıcaklık Desteği', en: 'Low Temperature Support' }, 
          value: { tr: '-30°C\'a kadar çalışma için tasarlanmıştır', en: 'Designed for operation down to -30°C' }
        },
        
        // Batarya
        { 
          label: { tr: 'Batarya Kapasitesi', en: 'Battery Capacity' }, 
          value: { tr: 'Li-ion 2800mAh / 10.08 Wh', en: 'Li-ion 2800mAh / 10.08 Wh' }
        },
        { 
          label: { tr: 'Batarya Ömrü', en: 'Battery Life' }, 
          value: { tr: '30+ Gün (60 dk iletim), 150+ Gün (24 Saat iletim)', en: '30+ Days (60 min interval), 150+ Days (24 hr interval)' }
        },
        
        // Genel
        { 
          label: { tr: 'Boyutlar', en: 'Dimensions' }, 
          value: { tr: '108 x 62 x 24 mm', en: '108 x 62 x 24 mm' }
        },
        { 
          label: { tr: 'Ağırlık', en: 'Weight' }, 
          value: { tr: '110 gram', en: '110 grams' }
        },
        { 
          label: { tr: 'Koruma', en: 'Protection' }, 
          value: { tr: 'IP67 (su ve toz geçirmez)', en: 'IP67 (waterproof and dustproof)' }
        },
        { 
          label: { tr: 'Raf Ömrü', en: 'Shelf Life' }, 
          value: { tr: '1 yıl', en: '1 year' }
        }
      ],
      useCases: {
        tr: [
          'İlaç ve aşı taşımacılığı',
          'Gıda lojistiği',
          'Laboratuvar örnekleri',
          'Soğuk zincir yönetimi',
          'Endüstriyel sıcaklık takibi',
          'Sera ve tarım ürünleri'
        ],
        en: [
          'Pharmaceutical and vaccine transport',
          'Food logistics',
          'Laboratory samples',
          'Cold chain management',
          'Industrial temperature tracking',
          'Greenhouse and agricultural products'
        ]
      },
      technologies: ['GSM/GPRS', 'Temperature Sensors', 'Cloud Platform', 'Web Dashboard']
    },
    'shalteron': {
      title: {
        tr: 'ShalterOn',
        en: 'ShalterOn'
      },
      category: {
        tr: 'Otomasyon',
        en: 'Automation'
      },
      description: {
        tr: '6 röle kontrolü ve 4 dijital input ile gelişmiş otomasyon sistemi. Bahçe sulama, sera kontrolü ve endüstriyel otomasyon için ideal çözüm. Mobil uygulama ile uzaktan kontrol imkanı sunar.',
        en: 'Advanced automation system with 6 relay controls and 4 digital inputs. Ideal solution for garden irrigation, greenhouse control and industrial automation. Offers remote control via mobile app.'
      },
      image: '/gsm_role.png',
      features: {
        tr: [
          '6 adet röle çıkışı (10A/250V)',
          '4 adet dijital input',
          'Mobil uygulama kontrolü',
          'Zamanlama ve programlama',
          'M2M SIM Kart',
          'Durum raporlama'
        ],
        en: [
          '6 relay outputs (10A/250V)',
          '4 digital inputs',
          'Mobile app control',
          'Scheduling and programming',
          'M2M SIM Card',
          'Status reporting'
        ]
      },
      specifications: [
        { 
          label: { tr: 'Bağlanabilir Cihaz Sayısı', en: 'Connectable Device Count' }, 
          value: { tr: '6 adet - NO/COM/NC', en: '6 units - NO/COM/NC' }
        },
        { 
          label: { tr: 'Kontrol Kapasitesi', en: 'Control Capacity' }, 
          value: { tr: '10A 250VAC | 10A 30VDC', en: '10A 250VAC | 10A 30VDC' }
        },
        { 
          label: { tr: 'Digital Input', en: 'Digital Input' }, 
          value: { tr: '4 adet (Enerji vermeyin!)', en: '4 units (Do not apply power!)' }
        },
        { 
          label: { tr: 'Bağlantı', en: 'Connection' }, 
          value: { tr: 'M2M SIM Kart | Hücresel Veri 10MB/Ay', en: 'M2M SIM Card | Cellular Data 10MB/Month' }
        },
        { 
          label: { tr: 'Anten', en: 'Antenna' }, 
          value: { tr: '3M', en: '3M' }
        },
        { 
          label: { tr: 'Güç Kaynağı', en: 'Power Supply' }, 
          value: { tr: '12V DC 1A Adaptör', en: '12V DC 1A Adapter' }
        },
        { 
          label: { tr: 'Boyutlar', en: 'Dimensions' }, 
          value: { tr: 'RT 207 Ray Tipi Kutu', en: 'RT 207 DIN Rail Box' }
        },
        { 
          label: { tr: 'Koruma', en: 'Protection' }, 
          value: { tr: 'IP54', en: 'IP54' }
        },
        { 
          label: { tr: 'Buzzer', en: 'Buzzer' }, 
          value: { tr: '1 adet', en: '1 unit' }
        }
      ],
      useCases: {
        tr: [
          'Bahçe sulama sistemleri',
          'Sera otomasyon',
          'Endüstriyel ekipman kontrolü',
          'Aydınlatma kontrolü',
          'Motor kontrol sistemleri',
          'Alarm sistemleri'
        ],
        en: [
          'Garden irrigation systems',
          'Greenhouse automation',
          'Industrial equipment control',
          'Lighting control',
          'Motor control systems',
          'Alarm systems'
        ]
      },
      technologies: ['GSM/GPRS', 'IoT', 'Mobile App', 'SMS Gateway', 'M2M SIM Card'],
      warnings: {
        tr: [
          '10 Amperden yüksek güçler için kontaktör kullanın.',
          'Okuma girişlerine 12V\'dan fazla enerji vermeyin!',
          'Cihaz girişleri kuru kontaktır. Enerji girişi yapmayın!',
          'Cihaz kurulumu için tecrübeli bir teknik servisten destek alın.'
        ],
        en: [
          'Use a contactor for powers higher than 10 Amps.',
          'Do not apply more than 12V to the input terminals!',
          'Device inputs are dry contacts. Do not apply power!',
          'Get support from an experienced technical service for device installation.'
        ]
      }
    },
    'akilli-gecis-sistemi': {
      title: {
        tr: 'Akıllı Geçiş Sistemi',
        en: 'Smart Access System'
      },
      category: {
        tr: 'Güvenlik Sistemleri',
        en: 'Security Systems'
      },
      description: {
        tr: 'QR kod ve mobil uygulama entegrasyonlu modern erişim kontrol sistemi. Okul, ofis ve fabrika girişleri için yoklama ve detaylı raporlama özellikleri sunar.',
        en: 'Modern access control system with QR code and mobile app integration. Offers attendance and detailed reporting features for school, office and factory entrances.'
      },
      image: '/kgs_1.png',
      features: {
        tr: [
          'Dinamik QR kod ile giriş',
          'RFID kart desteği',
          'Mobil uygulama entegrasyonu',
          'Yoklama sistemi',
          'Detaylı raporlama',
          'Zaman dilimi kontrolü'
        ],
        en: [
          'Dynamic QR code entry',
          'RFID card support',
          'Mobile app integration',
          'Attendance system',
          'Detailed reporting',
          'Time zone control'
        ]
      },
      specifications: [
        { 
          label: { tr: 'Kullanıcı Kapasitesi', en: 'User Capacity' }, 
          value: { tr: '20.000 kişi', en: '20,000 people' }
        },
        { 
          label: { tr: 'Kayıt Kapasitesi', en: 'Record Capacity' }, 
          value: { tr: '100.000 kayıt', en: '100,000 records' }
        },
        { 
          label: { tr: 'Tanımlama Yöntemleri', en: 'Identification Methods' }, 
          value: { tr: 'Proximity kart, Mifare kart, Mobil barkod', en: 'Proximity card, Mifare card, Mobile barcode' }
        },
        { 
          label: { tr: 'Kart Tipi', en: 'Card Type' }, 
          value: { tr: 'RFID 13.56MHz, QR Code', en: 'RFID 13.56MHz, QR Code' }
        },
        { 
          label: { tr: 'Okuma Mesafesi', en: 'Reading Distance' }, 
          value: { tr: '5-10 cm (kart) , barkod okuyucu destekli', en: '5-10 cm (card), barcode reader supported' }
        },
        { 
          label: { tr: 'Ekran', en: 'Display' }, 
          value: { tr: '2.4" Renkli TFT Ekran', en: '2.4" Color TFT Display' }
        },
        { 
          label: { tr: 'Bağlantı', en: 'Connection' }, 
          value: { tr: 'Ethernet / WiFi / RS-485 / USB', en: 'Ethernet / WiFi / RS-485 / USB' }
        },
        { 
          label: { tr: 'Güç', en: 'Power' }, 
          value: { tr: '12V DC 500mA', en: '12V DC 500mA' }
        },
        { 
          label: { tr: 'Boyutlar', en: 'Dimensions' }, 
          value: { tr: '200x150x40 mm', en: '200x150x40 mm' }
        },
        { 
          label: { tr: 'Ek Fonksiyonlar', en: 'Additional Functions' }, 
          value: { tr: 'Anti-pass Back, Zaman Dilimi Tanımlama, Uyku Modu', en: 'Anti-pass Back, Time Zone Definition, Sleep Mode' }
        }
      ],
      useCases: {
        tr: [
          'Okul Giriş Kontrolü',
          'Ofis Personel Takibi',
          'Fabrika Vardiya Kontrolü',
          'Etkinlik Katılımcı Takibi',
          'Kütüphane Geçiş Sistemi',
          'Hastane Ziyaretçi Sistemi'
        ],
        en: [
          'School Access Control',
          'Office Personnel Tracking',
          'Factory Shift Control',
          'Event Participant Tracking',
          'Library Access System',
          'Hospital Visitor System'
        ]
      },
      detailedUseCases: [
        {
          title: {
            tr: 'Turnike Geçiş Sistemleri',
            en: 'Turnstile Access Systems'
          },
          description: {
            tr: 'İlimera PDKS cihazı, personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar. Kart veya mobil barkod okutulduğunda turnike otomatik açılır, yoğun saatlerde bile hızlı ve düzenli geçiş imkânı sunar. Tüm geçişler anlık olarak sisteme kaydedilir ve istenildiğinde detaylı rapor alınabilir. Bu sayede kurumlarda güvenlik ve devam kontrolü kolayca sağlanır.',
            en: 'İlimera PDKS device ensures safe and controlled passage of personnel and students through turnstiles. The turnstile opens automatically when a card or mobile barcode is scanned, providing fast and orderly passage even during busy hours. All passages are instantly recorded in the system and detailed reports can be obtained when needed. This way, security and attendance control in institutions is easily ensured.'
          },
          points: {
            tr: [
              'Personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar.',
              'Kart veya mobil QR kod ile geçiş imkânı sunar.',
              'Yoğun saatlerde bile hızlı ve düzenli geçiş sağlar.',
              'Tüm geçişler anlık olarak sisteme kaydedilir.',
              'Yetkisiz girişler engellenir.',
              'Detaylı rapor alınabilir.',
              'Kurumlarda güvenlik ve devam kontrolünü kolaylaştırır.'
            ],
            en: [
              'Ensures secure and controlled passage for staff and students through turnstiles.',
              'Provides access using cards or mobile QR codes.',
              'Ensures fast and orderly passage even during peak hours.',
              'All passages are instantly recorded in the system.',
              'Unauthorized entries are prevented.',
              'Detailed reports can be obtained.',
              'Facilitates security and attendance control in institutions.'
            ]
          }
        },
        {
          title: {
            tr: 'Kapı Geçiş Sistemleri',
            en: 'Door Access Systems'
          },
          description: {
            tr: 'İlimera PDKS cihazı, kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar. Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile kolayca giriş yapabilir. Yetkisiz erişim denemeleri ise cihaz tarafından engellenir; sesli ve görsel uyarılar devreye girer ve sistem yöneticisine bildirim gönderilir. Tüm giriş-çıkışlar anlık kaydedilir ve detaylı raporlar oluşturularak kurum güvenliği ve takip süreçleri güçlendirilir.',
            en: 'İlimera PDKS device provides secure and controlled access at door entry-exit points in institutions. Authorized personnel or students can easily enter with card or mobile barcode. Unauthorized access attempts are blocked by the device; audible and visual alerts are activated and notifications are sent to the system administrator. All entries and exits are instantly recorded and detailed reports are created to strengthen institutional security and tracking processes.'
          },
          points: {
            tr: [
              'Kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar.',
              'Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile giriş yapabilir.',
              'Yetkisiz erişim denemeleri cihaz tarafından engellenir.',
              'Sesli ve görsel uyarılar devreye girer.',
              'Sistem yöneticisine bildirim gönderilir.',
              'Tüm giriş-çıkışlar anlık olarak sisteme kaydedilir.',
              'Detaylı raporlar oluşturularak güvenlik ve takip süreçleri güçlendirilir.'
            ],
            en: [
              'Ensures secure and controlled access at door entry-exit points in institutions.',
              'Authorized personnel or students can enter with a card or mobile barcode.',
              'Unauthorized access attempts are blocked by the device.',
              'Audible and visual warnings are activated.',
              'Notifications are sent to the system administrator.',
              'All entries and exits are instantly recorded in the system.',
              'Detailed reports are generated, strengthening security and monitoring processes.'
            ]
          }
        },
        {
          title: {
            tr: 'Yemekhane Kullanımı',
            en: 'Cafeteria Usage'
          },
          description: {
            tr: 'İlimera PDKS cihazı, yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder. Kart veya mobil barkod ile geçiş yapılabilir; sistem yalnızca yetkili kişilerin girişine izin verir. Bu sayede yemek israfı ve yetkisiz kullanım önlenir. Tüm girişler anlık kaydedilir ve yöneticiler detaylı raporlara ulaşabilir. Yemekhane kullanımı böylece düzenli, denetlenebilir ve adil şekilde yönetilir.',
            en: 'İlimera PDKS device quickly and securely checks meal rights of personnel and students at cafeteria entrances. Access can be made with card or mobile barcode; the system only allows entry of authorized persons. This prevents food waste and unauthorized use. All entries are instantly recorded and administrators can access detailed reports. Cafeteria usage is thus managed in an organized, auditable and fair manner.'
          },
          points: {
            tr: [
              'Yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder.',
              'Kart veya mobil barkod ile geçiş imkânı sunar.',
              'Yalnızca yetkili kişilerin girişine izin verir.',
              'Yemek israfı ve yetkisiz kullanımın önüne geçer.',
              'Tüm girişler anlık olarak sisteme kaydedilir.',
              'Yöneticiler detaylı raporlara ulaşabilir.',
              'Yemekhane kullanımı düzenli, denetlenebilir ve adil şekilde yönetilir.'
            ],
            en: [
              'Quickly and securely checks meal rights of personnel and students at cafeteria entrances.',
              'Provides access using cards or mobile barcodes.',
              'Only allows entry of authorized persons.',
              'Prevents food waste and unauthorized use.',
              'All entries are instantly recorded in the system.',
              'Administrators can access detailed reports.',
              'Cafeteria usage is managed in an organized, auditable and fair manner.'
            ]
          }
        },
        {
          title: {
            tr: 'Toplantı Odası Geçiş Kontrolü',
            en: 'Meeting Room Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, toplantı odalarına yalnızca yetkili personelin girişini sağlar. Kart veya mobil barkod ile erişim sağlanır, yetkisiz giriş denemeleri engellenir ve gerekirse alarm veya uyarı gönderilir. Tüm giriş-çıkışlar kaydedilir; hangi personelin hangi saatlerde odayı kullandığı raporlanabilir. Böylece güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.',
            en: 'İlimera PDKS device ensures that only authorized personnel can enter meeting rooms. Access is provided with card or mobile barcode, unauthorized entry attempts are blocked and alarms or warnings are sent if necessary. All entries and exits are recorded; which personnel used the room at what times can be reported. Thus, security is increased and internal resources are managed efficiently.'
          },
          points: {
            tr: [
              'Toplantı odalarına yalnızca yetkili personelin girişini sağlar.',
              'Kart veya mobil barkod ile erişim imkânı sunar.',
              'Yetkisiz giriş denemeleri engellenir.',
              'Gerekirse alarm veya uyarı gönderilir.',
              'Tüm giriş-çıkışlar kaydedilir.',
              'Hangi personelin hangi saatlerde odayı kullandığı raporlanabilir.',
              'Güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.'
            ],
            en: [
              'Ensures that only authorized personnel can enter meeting rooms.',
              'Provides access using cards or mobile barcodes.',
              'Unauthorized entry attempts are blocked.',
              'Alarms or warnings are sent if necessary.',
              'All entries and exits are recorded.',
              'Which personnel used the room at what times can be reported.',
              'Security is increased and internal resources are managed efficiently.'
            ]
          }
        },
        {
          title: {
            tr: 'Kütüphane Geçiş Kontrolü',
            en: 'Library Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar. Kart veya mobil barkod ile giriş yapılabilir; yalnızca kayıtlı kullanıcılar erişebilir, yetkisiz girişler engellenir. Kapasite sınırlaması sayesinde kütüphanedeki kişi sayısı kontrol edilir. Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir ve kullanım verimliliği artar. Tüm giriş-çıkışlar kaydedilir ve yöneticilere raporlama imkânı sunulur.',
            en: 'İlimera PDKS device ensures safe and orderly access of members at library entrances. Entry can be made with card or mobile barcode; only registered users can access, unauthorized entries are blocked. The number of people in the library is controlled through capacity limitation. Entry rights can be defined in advance with the reservation feature and usage efficiency increases. All entries and exits are recorded and reporting capability is provided to administrators.'
          },
          points: {
            tr: [
              'Kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar.',
              'Kart veya mobil barkod ile giriş imkânı sunar.',
              'Yalnızca kayıtlı kullanıcılar erişebilir; yetkisiz girişler engellenir.',
              'Kapasite sınırlaması ile kütüphanedeki kişi sayısı kontrol edilir.',
              'Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir, kullanım verimliliği artar.',
              'Tüm giriş-çıkışlar kaydedilir.',
              'Yöneticilere raporlama imkânı sunar.'
            ],
            en: [
              'Ensures safe and orderly access of members at library entrances.',
              'Provides entry using cards or mobile barcodes.',
              'Only registered users can access; unauthorized entries are blocked.',
              'The number of people in the library is controlled through capacity limitation.',
              'Entry rights can be defined in advance with the reservation feature, increasing usage efficiency.',
              'All entries and exits are recorded.',
              'Provides reporting capability to administrators.'
            ]
          }
        },
        {
          title: {
            tr: 'Etkinlik Geçiş Kontrolü',
            en: 'Event Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar. Katılımcılar bilet veya mobil QR kod ile giriş yapabilir; uzun kuyruklar önlenir ve düzenli geçiş sağlanır. Tüm girişler anlık kaydedilir, katılımcı sayısı takip edilir ve kapasite kontrolü yapılır. Yetkisiz girişler engellenerek güvenlik üst seviyede tutulur.',
            en: 'İlimera PDKS device provides fast and secure entry at concerts, fairs, seminars and similar events. Participants can enter with tickets or mobile QR codes; long queues are prevented and orderly passage is ensured. All entries are instantly recorded, participant numbers are tracked and capacity control is performed. Security is kept at the highest level by blocking unauthorized entries.'
          },
          points: {
            tr: [
              'Konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar.',
              'Katılımcılar bilet veya mobil QR kod ile giriş yapabilir.',
              'Uzun kuyruklar önlenir ve düzenli geçiş sağlanır.',
              'Tüm girişler anlık kaydedilir.',
              'Katılımcı sayısı takip edilir ve kapasite kontrolü yapılır.',
              'Yetkisiz girişler engellenerek güvenlik üst seviyede tutulur.'
            ],
            en: [
              'Provides fast and secure entry at concerts, fairs, seminars and similar events.',
              'Participants can enter with tickets or mobile QR codes.',
              'Long queues are prevented and orderly passage is ensured.',
              'All entries are instantly recorded.',
              'Participant numbers are tracked and capacity control is performed.',
              'Security is kept at the highest level by blocking unauthorized entries.'
            ]
          }
        },
        {
          title: {
            tr: 'Hızlı Kasa Geçiş Kontrolü',
            en: 'Fast Checkout Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, market, mağaza ve self-servis alanlarında müşterilerin kasa işlemlerini hızlı ve güvenli şekilde tamamlamasını sağlar. Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir; yoğun saatlerde kuyruklar önlenir ve müşteri memnuniyeti artar. Tüm geçişler kaydedilir, güvenlik ve satış takibi kolaylaşır. Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.',
            en: 'İlimera PDKS device enables customers to complete checkout transactions quickly and securely in markets, stores and self-service areas. Access can be made with shopping receipt or mobile QR code; queues are prevented during busy hours and customer satisfaction increases. All passages are recorded, making security and sales tracking easier. Transaction intensity can be analyzed with daily, weekly or monthly reports.'
          },
          points: {
            tr: [
              'Market, mağaza ve self-servis alanlarında müşterilerin kasa işlemlerini hızlı ve güvenli şekilde tamamlamasını sağlar.',
              'Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir.',
              'Yoğun saatlerde kuyruklar önlenir ve müşteri memnuniyeti artar.',
              'Tüm geçişler kaydedilir.',
              'Güvenlik ve satış takibi kolaylaşır.',
              'Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.'
            ],
            en: [
              'Enables customers to complete checkout transactions quickly and securely in markets, stores and self-service areas.',
              'Access can be made with shopping receipt or mobile QR code.',
              'Queues are prevented during busy hours and customer satisfaction increases.',
              'All passages are recorded.',
              'Security and sales tracking become easier.',
              'Transaction intensity can be analyzed with daily, weekly or monthly reports.'
            ]
          }
        }
      ],
      technologies: ['RFID', 'QR Code', 'TCP/IP', 'Mobile App', 'Cloud Database']
    },
    'depo-takip-sistemi': {
      title: {
        tr: 'Depo Takip Sistemi',
        en: 'Warehouse Tracking System'
      },
      category: {
        tr: 'Lojistik Çözümleri',
        en: 'Logistics Solutions'
      },
      description: {
        tr: 'Forklift ve depo operasyonları için geliştirilmiş anlık takip sistemi. Sensör tabanlı konum belirleme ve kullanıcı yetkilendirme sistemi ile depo verimliliğini artırır.',
        en: 'Real-time tracking system developed for forklift and warehouse operations. Increases warehouse efficiency with sensor-based location detection and user authorization system.'
      },
      image: 'https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBmb3JrbGlmdCUyMHRyYWNraW5nfGVufDF8fHx8MTc1Njg4MzQwOHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: {
        tr: [
          'Anlık Forklift Konumu',
          'Çalışma Süresi Takibi',
          'Kullanıcı Kimlik Doğrulama',
          'Rota Optimizasyonu',
          'Performans Analizi',
          'Güvenlik Alarm Sistemi'
        ],
        en: [
          'Real-time Forklift Location',
          'Working Time Tracking',
          'User Authentication',
          'Route Optimization',
          'Performance Analysis',
          'Security Alarm System'
        ]
      },
      specifications: [
        { 
          label: { tr: 'Konum Hassasiyeti', en: 'Location Accuracy' }, 
          value: { tr: '±1 metre', en: '±1 meter' }
        },
        { 
          label: { tr: 'Güncelleme Sıklığı', en: 'Update Frequency' }, 
          value: { tr: '1 saniye', en: '1 second' }
        },
        { 
          label: { tr: 'Bağlantı', en: 'Connection' }, 
          value: { tr: 'Wi-Fi, Bluetooth', en: 'Wi-Fi, Bluetooth' }
        },
        { 
          label: { tr: 'Batarya Ömrü', en: 'Battery Life' }, 
          value: { tr: '8-12 saat', en: '8-12 hours' }
        },
        { 
          label: { tr: 'Çalışma Sıcaklığı', en: 'Operating Temperature' }, 
          value: { tr: '-20°C ile +60°C', en: '-20°C to +60°C' }
        },
        { 
          label: { tr: 'Koruma', en: 'Protection' }, 
          value: { tr: 'IP67', en: 'IP67' }
        }
      ],
      useCases: {
        tr: [
          'Depo Forklift Takibi',
          'Operasyon Verimliliği',
          'Güvenlik Monitoring',
          'Maliyet Optimizasyonu',
          'İş Sağlığı ve Güvenliği'
        ],
        en: [
          'Warehouse Forklift Tracking',
          'Operational Efficiency',
          'Security Monitoring',
          'Cost Optimization',
          'Occupational Health and Safety'
        ]
      },
      technologies: ['GPS/GNSS', 'Wi-Fi', 'IoT Sensors', 'Real-time Analytics']
    },
    'sanal-capa-projesi': {
      title: {
        tr: 'Sanal Çapa Projesi',
        en: 'Virtual Anchor Project'
      },
      category: {
        tr: 'Denizcilik Teknolojisi',
        en: 'Maritime Technology'
      },
      description: {
        tr: 'Denizcilik sektörü için geliştirilen innovatif çözüm. Fiziksel çapaya ihtiyaç olmadan teknenin GPS teknolojisi ile sabit konumda kalmasını sağlar.',
        en: 'Innovative solution developed for the maritime industry. Keeps the boat in a fixed position using GPS technology without the need for a physical anchor.'
      },
      image: 'https://images.unsplash.com/photo-1738339439832-860548f03cc4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxib2F0JTIwYW5jaG9yJTIwbWFyaW5lJTIwdGVjaG5vbG9neXxlbnwxfHx8fDE3NTY4ODM0MDh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: {
        tr: [
          'GPS Tabanlı Sabit Durma',
          'Otomatik Konum Düzeltme',
          'Mobil Uygulama Kontrolü',
          'Kumanda ile Yön Kontrolü',
          'Planlı Rota Sistemi',
          'Acil Durum Modu'
        ],
        en: [
          'GPS-Based Stationary Positioning',
          'Automatic Position Correction',
          'Mobile App Control',
          'Remote Direction Control',
          'Planned Route System',
          'Emergency Mode'
        ]
      },
      specifications: [
        { 
          label: { tr: 'GPS Hassasiyeti', en: 'GPS Accuracy' }, 
          value: { tr: '±2 metre', en: '±2 meters' }
        },
        { 
          label: { tr: 'Çalışma Süresi', en: 'Operating Time' }, 
          value: { tr: '6-8 saat', en: '6-8 hours' }
        },
        { 
          label: { tr: 'Kontrol Mesafesi', en: 'Control Range' }, 
          value: { tr: '500 metre', en: '500 meters' }
        },
        { 
          label: { tr: 'Su Geçirmezlik', en: 'Waterproofing' }, 
          value: { tr: 'IPX8', en: 'IPX8' }
        },
        { 
          label: { tr: 'Tekne Boyutu', en: 'Boat Size' }, 
          value: { tr: '8-15 metre', en: '8-15 meters' }
        },
        { 
          label: { tr: 'Deniz Durumu', en: 'Sea Condition' }, 
          value: { tr: '4 deniz miline kadar', en: 'Up to sea state 4' }
        }
      ],
      useCases: {
        tr: [
          'Tekne Demirleme',
          'Balıkçı Tekneleri',
          'Yat ve Yelkenli Tekneler',
          'Arama Kurtarma',
          'Bilimsel Araştırma Tekneleri'
        ],
        en: [
          'Boat Anchoring',
          'Fishing Boats',
          'Yachts and Sailboats',
          'Search and Rescue',
          'Scientific Research Vessels'
        ]
      },
      technologies: ['GPS/GNSS', 'Marine Electronics', 'Wireless Control', 'Mobile App']
    }
  };

  const project = projects[slug as keyof typeof projects];

  if (!project) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{t.projectNotFound}</h1>
        <p className="text-gray-600 mb-8">{t.projectNotFoundDesc}</p>
        <Link to="/urunler">
          <Button>
            <ArrowLeft className="mr-2" size={16} />
            {t.backToProducts}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <Link to="/urunler" className="flex items-center text-primary hover:text-primary/90 transition-colors">
          <ArrowLeft className="mr-2" size={16} />
          {t.backToProducts}
        </Link>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="mb-4 bg-muted text-primary">
              {getLocalized(project.category)}
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {getLocalized(project.title)}
            </h1>
            <p className="text-lg text-muted leading-relaxed mb-8">
              {getLocalized(project.description)}
            </p>
            <div className="flex flex-col space-y-6">
            
              {slug === 'frigolive' && (
                <Link to="/downloads/frigolive">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}

              {slug === 'shalteron' && (
                <Link to="/downloads/shalteron">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}
              
              {slug === 'akilli-gecis-sistemi' && (
                <Link to="/downloads/akilli-gecis-sistemi">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <div>
            {slug === 'shalteron' ? (
              <img
                src='/gsm_role.png'
                alt={getLocalized(project.title)}
                className="w-full h-auto rounded-lg shadow-xl"
              />
            ) : (
              <ImageWithFallback
                src={project.image}
                alt={getLocalized(project.title)}
                className="w-full h-auto rounded-lg shadow-xl"
              />
            )}
          </div>
        </div>
      </section>

      {/* Product Video (ShalterOn) */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="rounded-lg overflow-hidden shadow-xl bg-black">
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/35G4v2CRiKQ?rel=0"
                title="ShalterOn Tanıtım Videosu"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">{t.features}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getLocalized(project.features).map((feature, index) => (
            <div key={index} className="flex items-center !bg-white dark:bg-white p-4 rounded-lg border">
              <CheckCircle className="text-primary mr-3 flex-shrink-0" size={20} />
              <span className="!text-muted dark:!text-white font-medium">{feature}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Specifications */}
      {slug !== 'akilli-gecis-sistemi' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.technicalSpecs}</h2>
          <Card>
            <CardContent className="p-6">
              {slug === 'frigolive' ? (
              <div className="space-y-6">
                {/* Bağlantı */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.connection}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(0, 1).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                        <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Konum */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.location}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(1, 3).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                        <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sensörler */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.sensors}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(3, 6).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                        <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sıcaklık ve Nem */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.temperatureHumidity}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(6, 13).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                        <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Batarya */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.battery}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(13, 15).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                        <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Genel */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.general}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(15).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                        <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {project.specifications.map((spec, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                    <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
      )}

      {/* Package Contents - ShalterOn */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.packageContents}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 {t.quantity}</p>
                    <p className="text-muted-foreground">{t.packageItem1}</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <Wifi className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 {t.quantity}</p>
                    <p className="text-muted-foreground">{t.packageItem2}</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 {t.quantity}</p>
                    <p className="text-muted-foreground">{t.packageItem3}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Warnings */}
      {project.warnings && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.warningsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getLocalized(project.warnings).map((warning, index) => (
              <Card key={index} className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
                <CardContent className="p-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 rounded-full mt-0.5 mr-3 flex-shrink-0 flex items-center justify-center shadow-md border border-orange-500">
                      <svg className="w-4 h-4 text-orange-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-orange-800 dark:text-orange-200 font-medium">{warning}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Use Cases */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">{t.useCases}</h2>
        {slug === 'akilli-gecis-sistemi' && project.detailedUseCases ? (
          <div className="space-y-6">
            {project.detailedUseCases.map((item, idx) => (
              <Card key={idx} className="border">
                <CardContent className="p-6">
                  {idx === 0 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/ticket-barriers-subway-entrance.jpg"
                          alt="Turnike Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 360 }}>
                        <img
                          src="/ticket-barriers-subway-entrance.jpg"
                          alt="Turnike Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 1 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/21228.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/21228.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 2 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/chef-preparing-recipe.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/chef-preparing-recipe.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 3 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/empty-hallway-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/empty-hallway-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ): idx === 4 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/young-student-learning-library.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/young-student-learning-library.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ): idx === 5 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/retro-theatre-scene-celebrate-world-theatre-day.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/retro-theatre-scene-celebrate-world-theatre-day.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ): idx === 6 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/abstract-blur-shopping-mall-retails-store-interior-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/abstract-blur-shopping-mall-retails-store-interior-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110"
                        />
                      </div>
                    </div>
                  ):(
                    <>
                      <div className="flex items-start mb-4">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                          <Settings className="text-primary" size={20} />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{getLocalized(item.description)}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getLocalized(project.useCases).map((useCase, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                    <Settings className="text-primary" size={20} />
                  </div>
                  <span className="text-foreground font-medium">{useCase}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
      
      {/* Product Documentation (ShalterOn) */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.productDocumentation}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">ShalterOn {language === 'tr' ? 'Kullanım Kılavuzu' : 'User Manual'}</h3>
                    <p className="text-muted-foreground">{language === 'tr' ? 'Detaylı kurulum ve kullanım bilgileri' : 'Detailed installation and usage information'}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => window.open('/ShalterOn_v1.5.pdf', '_blank', 'noopener,noreferrer')}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {t.viewPdf}
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      )}
      
      {/* Technologies */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">{t.technologies}</h2>
        <div className="flex flex-wrap gap-3">
          {project.technologies.map((tech) => (
            <Badge key={tech} variant="outline" className="px-4 py-2 text-sm">
              {tech}
            </Badge>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {slug === 'frigolive' && (
                <Button 
                  size="lg" 
                  className="group self-start"
                  onClick={() => window.open('https://frigolive.com/', '_blank', 'noopener,noreferrer')}
                >
                  {t.visitWebsite}
                </Button>
              )}

            <Link to="/iletisim">
              <Button
                size="lg"
                variant="outline"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white"
              >
                {t.contactUs}
              </Button>
            </Link>

            <Link to="/urunler">
              <Button
                variant="outline"
                size="lg"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white">
                {t.viewOtherProjects}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}